import { useForm, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ERuntimeSettings } from '../../common/types/runtimeSettings';
import { useApplicationContext } from '../../common/utils/applicationContext';
import { DEFAULT_FORM_STATE } from '../constants/defaultFormState';
import { setDisplayAllFilters, setSelectedRegion } from '../redux';
import { selectDefaultFormState, selectRegionOptions } from '../selectors';
import { IFormState, TElement } from '../types/calculator';
import { getInitialCalculatorFormState, getInitialSelectedRegion } from '../utils/getInitialCalculatorFormState';
import { useCalculatorCustomEvents } from './useCalculatorCustomEvents';
import { useCalculatorResetEvent } from './useCalculatorResetEvent';

export const useCalculatorForm = (formScheme: TElement[], formRef: HTMLFormElement | null) => {
  const dispatch = useDispatch();
  const { config } = useApplicationContext();
  const preselectedFormValues = useSelector(selectDefaultFormState);
  const regionsOptions = useSelector(selectRegionOptions);
  const isMortgageLandingRedesignEnabled =
    config.get<boolean>(ERuntimeSettings.mortgageLandingRedesignEnabled) ?? false;

  const defaultValues = useMemo(() => getInitialCalculatorFormState(preselectedFormValues), [preselectedFormValues]);

  const reactHookForm = useForm<IFormState>({
    mode: 'onChange',
    defaultValues,
  });

  const { formState, reset, control, getValues } = reactHookForm;
  const watchedFormValues = useWatch({ control }) as IFormState;
  // Явно читаем formState, чтобы RHF доставлял обновления в этот рендер (см. форму RHF 7+).
  const { isValid, errors, isDirty, dirtyFields, touchedFields, submitCount } = formState;

  // `reset` только по смене *содержимого* defaults (не по новой ссылке на тот же объект) — иначе поля ввода сбрасываются.
  const preselectedKey = useMemo(() => JSON.stringify(preselectedFormValues ?? null), [preselectedFormValues]);
  const preselectedRef = useRef(preselectedFormValues);
  preselectedRef.current = preselectedFormValues;
  // установка дефолтных значение при инициализации
  useEffect(() => {
    reset({
      ...DEFAULT_FORM_STATE,
      ...preselectedRef.current,
    });
  }, [preselectedKey, reset]);

  // TODO: данный хук добавляет дополнительные рендеры, нужно оптимизировать
  const { notifyFormReset } = useCalculatorCustomEvents({
    getValues,
    watchedFormValues,
    isValid,
    errors,
    isDirty,
    dirtyFields,
    touchedFields,
    submitCount,
    eventTarget: formRef,
    formScheme,
  });

  const handleResetToInitial = useCallback(() => {
    const initialValues = getInitialCalculatorFormState(preselectedRef.current);

    reset(initialValues, { keepDefaultValues: true });
    dispatch(setSelectedRegion(getInitialSelectedRegion(initialValues.regionId, regionsOptions)));
    dispatch(setDisplayAllFilters(isMortgageLandingRedesignEnabled));
    notifyFormReset();
  }, [dispatch, isMortgageLandingRedesignEnabled, notifyFormReset, regionsOptions, reset]);

  useCalculatorResetEvent({
    eventTarget: formRef,
    onReset: handleResetToInitial,
  });

  return { reactHookForm };
};
